import { useEffect, useMemo, useRef, useState, type MouseEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ConsoleShell, PageHead } from "../components/ConsoleShell.js";
import { Tag, BtnLink, Btn } from "../components/index.js";
import { Frame } from "../components/Frame.js";
import { LoadingState, ErrorState, EmptyState } from "../components/EmptyState.js";
import { Icon } from "../components/Icon.js";
import { ProgressRing } from "../components/ProgressRing.js";
import { useAsync } from "../lib/useAsync.js";
import { manuals as manualsApi, books as booksApi, stores as storesApi, hasRole, mediaUrl } from "../lib/api.js";
import { isOrphanDraft } from "../lib/manualFilters.js";
import { takeOpening } from "../lib/bookTransition.js";
import { readPct, isComplete } from "../lib/readingProgress.js";
import type { Book, Manual, Store } from "@tebiki/shared";

function statusTag(m: Manual) {
  // *-ink tokens: the plain state colors are ~3:1 on paper — too faint for 10.5px tag text.
  switch (m.status) {
    case "published":
      return <Tag dot color="var(--ok-ink)">公開中</Tag>;
    case "review":
      return <Tag hi>承認待ち</Tag>;
    case "processing":
      return <Tag dot color="var(--info-ink)">AI処理中</Tag>;
    case "draft":
      return <Tag>下書き</Tag>;
    default:
      return <Tag>{m.status}</Tag>;
  }
}

type BookData = [Book, Manual[], Store[]];

/**
 * /book/:id — an OPENED 本: its manuals as an ordered table-of-contents (the array order IS the
 * reading order). Editors can reorder / remove / append manuals and manage the book itself
 * (必読・対象店舗・説明・削除). FLIP-morphs in from the tapped cover (see bookTransition).
 */
export function BookView() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const canEdit = hasRole("owner", "admin", "editor");

  const { data, loading, error, offline, reload } = useAsync<BookData>(
    async () => {
      const [book, list, stores] = await Promise.all([
        booksApi.get(id),
        manualsApi.list().then((r) => r.items.filter((m) => !isOrphanDraft(m.status, m.title))),
        storesApi.list().catch(() => [] as Store[]),
      ]);
      return [book, list, stores];
    },
    [id],
  );
  const [book, allManuals, stores] = data ?? [null, [], []];

  const canReadUnpublished = hasRole("owner", "admin", "editor", "reviewer");
  const manualById = useMemo(() => new Map(allManuals.map((m) => [m.manualId, m])), [allManuals]);
  const contents = useMemo(() => {
    if (!book) return [];
    const list = book.manualIds.map((mid) => manualById.get(mid)).filter((m): m is Manual => !!m);
    // 現場閲覧者には公開済みのみ（承認前の内容を本経由で見せない）。
    return canReadUnpublished ? list : list.filter((m) => m.status === "published");
  }, [book, manualById, canReadUnpublished]);
  const outside = useMemo(
    () => (book ? allManuals.filter((m) => !book.manualIds.includes(m.manualId)) : []),
    [book, allManuals],
  );

  const [busy, setBusy] = useState(false);
  const [managing, setManaging] = useState(false);
  const [addId, setAddId] = useState("");
  const [descDraft, setDescDraft] = useState<string | null>(null);
  const [liveMsg, setLiveMsg] = useState(""); // sr-only announcements (並び替え結果)

  /** Run a book mutation then refetch. Returns true only on success (callers keep drafts on failure). */
  async function mutate(fn: () => Promise<unknown>, failMsg: string): Promise<boolean> {
    if (busy) return false;
    setBusy(true);
    try {
      await fn();
      await reload();
      return true;
    } catch {
      alert(failMsg);
      return false;
    } finally {
      setBusy(false);
    }
  }

  function move(index: number, dir: -1 | 1) {
    if (!book) return;
    const target = contents[index];
    const neighbor = contents[index + dir];
    if (!target || !neighbor) return;
    // Swap by ID, not index: book.manualIds may hold entries hidden from `contents`
    // (deleted manuals, orphan drafts), so list indexes don't map 1:1 onto the array.
    const ids = [...book.manualIds];
    const a = ids.indexOf(target.manualId);
    const b = ids.indexOf(neighbor.manualId);
    if (a < 0 || b < 0) return;
    [ids[a], ids[b]] = [ids[b], ids[a]];
    void mutate(() => booksApi.setItems(book.bookId, ids), "並び替えに失敗しました。").then((ok) => {
      if (!ok) return;
      setLiveMsg(`「${target.title}」を${index + 1 + dir}番目に移動しました`);
      // Put focus back on the moved row's button (the old one unmounted with the reload).
      requestAnimationFrame(() =>
        document.querySelector<HTMLButtonElement>(`[data-move="${target.manualId}:${dir}"]`)?.focus(),
      );
    });
  }

  function removeItem(e: MouseEvent, manualId: string, title: string) {
    e.preventDefault();
    if (!book) return;
    if (!confirm(`「${title}」をこの本から外しますか？（マニュアル自体は削除されません）`)) return;
    void mutate(() => booksApi.removeItem(book.bookId, manualId), "本から外せませんでした。");
  }

  function toggleStore(storeId: string) {
    if (!book) return;
    // Fetch-fresh-then-toggle: the panel may render a stale book while a refetch is in flight;
    // computing `next` from that stale audience would silently drop the previous toggle.
    void mutate(async () => {
      const fresh = await booksApi.get(book.bookId);
      const cur = fresh.audience.storeIds;
      const next = cur.includes(storeId) ? cur.filter((s) => s !== storeId) : [...cur, storeId];
      await booksApi.update(book.bookId, { audience: { storeIds: next } });
    }, "対象店舗を更新できませんでした。");
  }

  async function deleteBook() {
    if (!book) return;
    if (!confirm(`本「${book.title}」を削除しますか？（中のマニュアルは削除されません）`)) return;
    setBusy(true);
    try {
      await booksApi.remove(book.bookId);
      navigate("/library");
    } catch {
      alert("本を削除できませんでした。");
      setBusy(false);
    }
  }

  // FLIP-in: morph the header from the tapped cover's rect (cleared on transitionend).
  const headerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const opening = takeOpening();
    const el = headerRef.current;
    if (!opening || !el) return;
    if (window.matchMedia("(prefers-reduced-motion:reduce)").matches) return;
    const to = el.getBoundingClientRect();
    const from = opening.rect;
    if (!to.width || !from.width) return;
    const dx = from.left - to.left;
    const dy = from.top - to.top;
    const sx = Math.max(0.2, from.width / to.width);
    el.style.transformOrigin = "top left";
    el.style.transform = `translate(${dx}px,${dy}px) scale(${sx})`;
    el.style.opacity = "0.45";
    const clear = () => {
      el.style.transition = "";
      el.style.transform = "";
      el.style.opacity = "";
      el.style.transformOrigin = "";
    };
    const raf = requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        el.style.transition = "transform .42s var(--ease), opacity .42s var(--ease)";
        el.style.transform = "none";
        el.style.opacity = "1";
      }),
    );
    const t = window.setTimeout(clear, 520);
    el.addEventListener(
      "transitionend",
      () => {
        clear();
        window.clearTimeout(t);
      },
      { once: true },
    );
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(t);
      clear(); // StrictMode double-mount: run #1's cleanup must undo the transform it applied
    };
  }, []);

  const glyph = (book?.title || "本").trim().charAt(0);
  const audienceLabel = book
    ? book.audience.storeIds.length === 0
      ? "全店共通"
      : book.audience.storeIds.map((sid) => stores.find((s) => s.id === sid)?.name ?? "店舗").join("・")
    : "";

  return (
    <ConsoleShell breadcrumb={["TEBIKI", "書架", book?.title ?? "本"]}>
      <span className="sr-only" role="status" aria-live="polite">
        {liveMsg}
      </span>
      <div ref={headerRef} style={{ position: "relative", willChange: "transform" }}>
        <span
          className="num num--outline"
          aria-hidden="true"
          style={{ position: "absolute", right: 6, top: -18, fontSize: "clamp(80px,18vw,150px)", opacity: 0.5, pointerEvents: "none" }}
        >
          {glyph}
        </span>
        <PageHead title={<><span className="hl">{book?.title ?? "本"}</span></>}>
          {book ? (
            <>
              対象: {audienceLabel} · {contents.length}冊
              {book.mustRead ? " · 必読" : ""}
              {book.description ? <> — {book.description}</> : null}
            </>
          ) : (
            " "
          )}
        </PageHead>
      </div>

      <div className="row" style={{ marginBottom: 16, gap: 10, flexWrap: "wrap" }}>
        <Link className="tag" to="/library" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <Icon name="arrow-left" size={14} /> 書架に戻る
        </Link>
        {canEdit && book ? (
          <button
            type="button"
            className="tag"
            aria-expanded={managing}
            onClick={() => {
              setManaging((v) => !v);
              setDescDraft(null);
            }}
            style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer" }}
          >
            <Icon name="settings" size={14} /> 本の設定
          </button>
        ) : null}
      </div>

      {canEdit && managing && book ? (
        <div className="card" style={{ padding: 18, marginBottom: 20, display: "grid", gap: 14 }}>
          <div>
            <span className="field-label" style={{ display: "block", marginBottom: 6 }}>対象店舗（未選択 = 全店共通）</span>
            <div className="row" role="group" aria-label="対象店舗" style={{ gap: 8, flexWrap: "wrap" }}>
              {stores.map((s) => {
                const on = book.audience.storeIds.includes(s.id);
                return (
                  <button
                    key={s.id}
                    type="button"
                    aria-pressed={on}
                    disabled={busy}
                    onClick={() => toggleStore(s.id)}
                    className="tag jp"
                    style={{
                      minHeight: 44,
                      padding: "8px 14px",
                      cursor: "pointer",
                      borderColor: on ? "var(--hi-deep)" : "var(--line-2)",
                      background: on ? "color-mix(in srgb,var(--hi) 16%,transparent)" : "transparent",
                      color: on ? "var(--ink)" : "var(--muted)",
                    }}
                  >
                    {s.name}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="row" style={{ gap: 10, flexWrap: "wrap" }}>
            <button
              type="button"
              aria-pressed={book.mustRead}
              disabled={busy || loading}
              onClick={() =>
                void mutate(async () => {
                  const fresh = await booksApi.get(book.bookId);
                  await booksApi.update(book.bookId, { mustRead: !fresh.mustRead });
                }, "必読設定を更新できませんでした。")
              }
              className="tag"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                minHeight: 44,
                padding: "8px 14px",
                cursor: "pointer",
                borderColor: book.mustRead ? "var(--hi-deep)" : "var(--line-2)",
                background: book.mustRead ? "color-mix(in srgb,var(--hi) 16%,transparent)" : "transparent",
              }}
            >
              <Icon name={book.mustRead ? "check" : "star"} size={14} /> 必読の本にする
            </button>
            <span className="grow" />
            <button
              type="button"
              className="tag"
              disabled={busy}
              onClick={() => void deleteBook()}
              style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer", color: "var(--danger)", borderColor: "color-mix(in srgb,var(--danger) 50%,transparent)" }}
            >
              <Icon name="close" size={14} /> 本を削除
            </button>
          </div>

          <div>
            <label className="field-label" htmlFor="book-desc">説明</label>
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              <input
                id="book-desc"
                className="input jp"
                style={{ flex: "1 1 260px" }}
                value={descDraft ?? book.description ?? ""}
                onChange={(e) => setDescDraft(e.target.value)}
                placeholder="この本の目的（例: 新人が最初の1週間で読む本）"
              />
              <Btn
                disabled={busy || descDraft === null}
                onClick={() =>
                  void mutate(
                    () => booksApi.update(book.bookId, { description: (descDraft ?? "").trim() || null }),
                    "説明を保存できませんでした。",
                  ).then((ok) => {
                    if (ok) setDescDraft(null); // keep the draft on failure — never eat the user's text
                  })
                }
              >
                保存
              </Btn>
            </div>
          </div>
        </div>
      ) : null}

      {loading ? (
        <LoadingState label="本を開いています…" />
      ) : error ? (
        <ErrorState message={error} offline={offline} onRetry={reload} />
      ) : !book ? (
        <EmptyState title="この本は見つかりません" />
      ) : contents.length === 0 ? (
        <EmptyState icon={<Icon name="library" size={30} />} title="この本はまだ空です">
          {canEdit
            ? "下の「マニュアルを追加」か、各マニュアルの提出時に本を選ぶと、ここに並びます。"
            : "マニュアルが公開されると、ここに並びます。"}
        </EmptyState>
      ) : (
        <div className="list reveal d1">
          <div className="lrow lhead chaprow">
            <span />
            <span>マニュアル（上から読む順）</span>
            <span>ステータス</span>
            <span />
          </div>
          {contents.map((m, i) => {
            const done = isComplete(m.manualId);
            const pct = readPct(m.manualId);
            return (
              <div key={m.manualId} className={`lrow chaprow reveal${i < 3 ? ` d${i + 1}` : ""}`} style={{ alignItems: "center" }}>
                <div className="row" style={{ gap: 6 }}>
                  <span className="mono" style={{ fontSize: 12, color: "var(--faint)", width: 22, textAlign: "right" }}>{i + 1}</span>
                  <Frame paper className="thumb" style={{ width: 48 }} src={mediaUrl(m.thumbnailUrl)} subject={m.status === "published"} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div className="display jp" style={{ fontSize: 17, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {m.title}
                  </div>
                  <div className="mono" style={{ fontSize: 11, color: "var(--muted)", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span>v{m.version ?? 1}</span>
                    <span>·</span>
                    <span>{m.sourceType === "video" ? "動画" : "画像"}</span>
                    {pct > 0 || done ? (
                      <>
                        <span>·</span>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <ProgressRing pct={pct} done={done} size={15} />
                          {done ? "修了" : `${pct}%`}
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
                <span style={{ justifySelf: "start" }}>{statusTag(m)}</span>
                <div className="row" style={{ gap: 6, justifyContent: "flex-end", flexWrap: "wrap" }}>
                  {canEdit ? (
                    <>
                      <button type="button" className="tag" data-move={`${m.manualId}:-1`} aria-label={`${m.title} を上へ`} disabled={busy || i === 0} onClick={() => move(i, -1)} style={{ cursor: "pointer" }}>
                        <Icon name="chevron" size={14} />
                      </button>
                      <button type="button" className="tag" data-move={`${m.manualId}:1`} aria-label={`${m.title} を下へ`} disabled={busy || i === contents.length - 1} onClick={() => move(i, 1)} style={{ cursor: "pointer" }}>
                        <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} />
                      </button>
                    </>
                  ) : null}
                  {m.status === "processing" ? (
                    <BtnLink to={`/processing/${m.manualId}`}>処理を見る</BtnLink>
                  ) : (
                    <>
                      <BtnLink to={`/read/${m.manualId}`} state={{ from: `/book/${book.bookId}` }} variant="primary" icon={<Icon name="play" size={16} />}>
                        読む
                      </BtnLink>
                      {canEdit ? (
                        <Link className="tag" to={`/preview/${m.manualId}`} state={{ from: `/book/${book.bookId}` }} title="編集" aria-label={`${m.title} を編集`}>
                          <Icon name="edit" size={15} />
                        </Link>
                      ) : null}
                    </>
                  )}
                  {canEdit ? (
                    <button
                      type="button"
                      onClick={(e) => removeItem(e, m.manualId, m.title)}
                      title="この本から外す"
                      aria-label={`${m.title} をこの本から外す`}
                      className="tag"
                      style={{ color: "var(--muted)", cursor: "pointer" }}
                      disabled={busy}
                    >
                      <Icon name="close" size={15} />
                    </button>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {canEdit && book ? (
        <div className="row" style={{ marginTop: 18, gap: 10, flexWrap: "wrap" }}>
          <label className="sr-only" htmlFor="book-add">この本に追加するマニュアル</label>
          <select id="book-add" className="select" style={{ width: "auto", minWidth: 240 }} value={addId} onChange={(e) => setAddId(e.target.value)}>
            <option value="">マニュアルを選んで追加…</option>
            {outside.map((m) => (
              <option key={m.manualId} value={m.manualId}>{m.title}</option>
            ))}
          </select>
          <Btn
            icon={<Icon name="plus" size={16} />}
            disabled={!addId || busy}
            onClick={() =>
              void mutate(() => booksApi.addItem(book.bookId, addId), "追加できませんでした。").then(() => setAddId(""))
            }
          >
            この本に追加
          </Btn>
        </div>
      ) : null}
    </ConsoleShell>
  );
}
